/* eslint-disable @typescript-eslint/no-explicit-any */
import { HttpEvent, HttpHandler, HttpInterceptor, HttpParams, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { isEmpty, isNil } from 'lodash';
import moment from 'moment-timezone';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { OspDateTime } from '../../shared/contract/osp-date-time.contract';
import { DatesService } from '../../shared/services/dates.service';
import { TimezoneService } from '../../shared/services/timezone.service';

@Injectable()
export class TimezoneInterceptor implements HttpInterceptor {
  private readonly timezoneService = inject(TimezoneService);

  // TODO: Temporary logging
  private static readonly storageKeyTZ = 'TIMEZONE_INTERCEPTOR_LOGS_ENABLED';
  private static ENABLE_LOGS = /^true$/i.test(localStorage.getItem(TimezoneInterceptor.storageKeyTZ) || '');

  static isLogsEnabled(): boolean {
    return TimezoneInterceptor.ENABLE_LOGS;
  }
  static toggleLogs(enable: boolean): void {
    this.ENABLE_LOGS = enable;
    localStorage.setItem(TimezoneInterceptor.storageKeyTZ, String(enable));
  }
  private consoleLog(...texts: string[]): void {
    if (TimezoneInterceptor.ENABLE_LOGS) {
      console.log(...texts);
    }
  }
  private consoleGroup(...texts: string[]): void {
    if (TimezoneInterceptor.ENABLE_LOGS) {
      console.group(...texts);
    }
  }
  private consoleGroupEnd(): void {
    if (TimezoneInterceptor.ENABLE_LOGS) {
      console.groupEnd();
    }
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (this.timezoneService.getUserTimezoneShiftMinutes() === 0) {
      // A timezone the same. Do nothing..
      return next.handle(req);
    }

    this.consoleLog('========== Timezone interceptor START ==========');
    this.consoleLog('URL: ', req.url);

    this.consoleGroup('REQUEST PARAMS');
    const params = this.handleRequestParams(req.params);
    this.consoleGroupEnd();

    this.consoleGroup('REQUEST BODY');
    const body = this.handleRequestBody(req.body);
    this.consoleGroupEnd();

    const modifiedRequest = req.clone({ body, params });

    // Pass the modified request and transform the response body
    return next.handle(modifiedRequest).pipe(
      map((event: HttpEvent<any>) => {
        if (event instanceof HttpResponse) {
          this.consoleGroup('RESPONSE BODY');
          const body = this.handleResponseBody(event.body);
          this.consoleGroupEnd();
          this.consoleLog('========== Timezone interceptor END ==========');
          return event.clone({ body });
        }
        return event;
      }),
    );
  }

  private handleRequestParams(params: HttpParams): HttpParams {
    let convertedParams = new HttpParams();

    params.keys().forEach((key) => {
      const values = params.getAll(key);
      if (values) {
        values.forEach((value) => {
          const converted = this.convertDates(value, false, key);
          convertedParams = convertedParams.append(key, converted);
        });
      }
    });

    return convertedParams;
  }

  private handleRequestBody(payload: any): any {
    return this.convertDates(payload, false, 'body');
  }

  private handleResponseBody(payload: any): any {
    return this.convertDates(payload, true, 'body');
  }

  private convertDates(payload: any, isInput: boolean, propertyName: string): any {
    if (isNil(payload) || isEmpty(payload)) {
      return payload;
    }

    if (typeof payload === 'string' && DatesService.isISOFormatDate(payload)) {
      // Date conversion
      const converted = this.shiftDate(payload, isInput);
      this.consoleLog(`CONVERSION [${propertyName}]: ${payload} --> ${converted}`);
      return converted;
    }

    if (this.isOspDateTime(payload)) {
      return this.shiftOspDateTime(payload, isInput);
    }

    if (typeof payload === 'string') {
      const t = payload.trim();
      if (t.startsWith('{') || t.startsWith('[')) {
        try {
          const parsed = JSON.parse(t);
          const converted = this.convertDates(parsed, isInput, propertyName);
          return JSON.stringify(converted);
        } catch {
          return payload;
        }
      }
      return payload;
    }

    if (Array.isArray(payload)) {
      return payload.map((item) => this.convertDates(item, isInput, propertyName));
    }

    return Object.keys(payload).reduce((acc: any, key: string) => {
      acc[key] = this.convertDates(payload[key], isInput, key);
      return acc;
    }, {});
  }

  private isOspDateTime(value: any): value is OspDateTime {
    return Boolean(value && (value as OspDateTime).timestamp && (value as OspDateTime).zoneId);
  }

  private shiftDate(date: Date | string, isInput: boolean): string {
    const offset = (isInput ? 1 : -1) * this.timezoneService.getUserTimezoneShiftMinutes();
    return moment(date).add(offset, 'minutes').toISOString();
  }

  private shiftOspDateTime(value: OspDateTime, isInput: boolean): OspDateTime {
    return {
      timestamp: this.shiftDate(value.timestamp, isInput),
      zoneId: isInput ? this.timezoneService.getLocalTimezone() : this.timezoneService.getUserTimezone(),
    };
  }
}
